/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Vault-passphrase escrow encryption for the printed-QR recovery flow.
 *
 * The frontend encrypts the user's vault passphrase to the system's ML-KEM-768
 * escrow public key. The bundle is rendered as a QR code (via `TdPrintQr`).
 * On a recovery request (`TdForgotPass`), the bundle is sent back to the
 * server, which holds the matching private key and decapsulates + decrypts.
 *
 * AAD-binding to the user's email means a stolen QR alone cannot recover a
 * different account's passphrase — the server reconstructs the AAD from the
 * JWT principal, and a mismatched email causes Poly1305 tag verification to
 * fail with no information leaked.
 *
 * Backend contract: beyonddemise/app-backend#94.
 * Caller: `src/components/TdPrintQr.js` (encryption).
 *
 * Conventions follow `src/services/document/routingsheet.ts:wrapForRecipient`:
 *   encapsulate → HKDF-SHA-256 → AEAD-encrypt with typed-field AAD.
 */

import { z } from 'zod';
import { base64nopad } from '@scure/base';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  encrypt as aeadEncrypt,
  decrypt as aeadDecrypt,
  AeadAuthenticationError,
} from './aead.js';
import {
  encapsulate as kemEncapsulate,
  decapsulate as kemDecapsulate,
} from './mlkem.js';
import { type Random } from '../random.js';

/** Versioned algorithm identifier — bump on protocol change (AAD format, primitive swap, etc.). */
export const ESCROW_ALGORITHM_ID = 'ML-KEM-768+XChaCha20-Poly1305-v1' as const;

/** Domain string used for both HKDF info and the AAD prefix. */
const RECOVERY_DOMAIN = 'BD-recovery-v1';
const RECOVERY_DOMAIN_BYTES = new TextEncoder().encode(RECOVERY_DOMAIN);

/** Output length of the derived AEAD key — XChaCha20-Poly1305 key size. */
const KEK_LENGTH_BYTES = 32;
/** XChaCha20-Poly1305 nonce size. */
const NONCE_LENGTH_BYTES = 24;

// -----------------------------------------------------------------------------
// On-wire schema
// -----------------------------------------------------------------------------

/**
 * The QR-payload shape. Encoded on the wire as base64nopad strings; the
 * `algorithm` field is the literal `ESCROW_ALGORITHM_ID` so future protocol
 * upgrades can refuse to decrypt old bundles cleanly.
 */
export const RecoveryBundleSchema = z
  .object({
    algorithm: z.literal(ESCROW_ALGORITHM_ID),
    kemCiphertext: z.string().min(1), // base64nopad of 1088 bytes
    nonce: z.string().min(1), // base64nopad of 24 bytes
    wrappedPassphrase: z.string().min(1), // base64nopad of (plaintext + 16-byte tag)
  })
  .strict();
export type RecoveryBundle = z.infer<typeof RecoveryBundleSchema>;

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------

/**
 * Thrown by `encryptForEscrow` and `decryptFromEscrow` when input shape or
 * crypto invariants fail. The `reason` discriminator lets callers
 * pattern-match without grepping message text.
 */
export class RecoveryError extends Error {
  readonly reason:
    | 'invalid-public-key'
    | 'invalid-private-key'
    | 'invalid-bundle-shape'
    | 'invalid-email'
    | 'authentication-failed';

  constructor(
    reason: RecoveryError['reason'],
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'RecoveryError';
    this.reason = reason;
  }
}

// -----------------------------------------------------------------------------
// AAD construction
// -----------------------------------------------------------------------------

/**
 * Build the AAD bound to the user's email. Format (typed-field byte
 * concatenation):
 *
 *   utf8('BD-recovery-v1') || 0x00 || u16-be(emailLen) || emailUtf8
 *
 * The same construction runs on both encrypt and decrypt sides; a mismatch
 * (wrong account's email) causes the Poly1305 tag verification to fail.
 *
 * Max email length: 65535 bytes (u16 limit). RFC 5321 caps the email address
 * length at 254 bytes, well under the limit.
 */
const buildRecoveryAad = (userEmail: string): Uint8Array => {
  if (!userEmail || typeof userEmail !== 'string') {
    throw new RecoveryError(
      'invalid-email',
      'userEmail must be a non-empty string'
    );
  }
  const emailBytes = new TextEncoder().encode(userEmail);
  if (emailBytes.length > 0xffff) {
    throw new RecoveryError(
      'invalid-email',
      `userEmail UTF-8 length ${emailBytes.length} exceeds u16 max`
    );
  }
  const aad = new Uint8Array(
    RECOVERY_DOMAIN_BYTES.length + 1 + 2 + emailBytes.length
  );
  let offset = 0;
  aad.set(RECOVERY_DOMAIN_BYTES, offset);
  offset += RECOVERY_DOMAIN_BYTES.length;
  aad[offset++] = 0x00; // delimiter
  aad[offset++] = (emailBytes.length >>> 8) & 0xff;
  aad[offset++] = emailBytes.length & 0xff;
  aad.set(emailBytes, offset);
  return aad;
};

// -----------------------------------------------------------------------------
// Encrypt
// -----------------------------------------------------------------------------

/**
 * Encrypt the user's vault passphrase under the system's ML-KEM-768 public
 * key, AAD-bound to the user's email.
 *
 * @param rng - randomness source (use `defaultRandom` in production)
 * @param systemKemPublicKey - 1184-byte ML-KEM-768 public key from
 *   `GET /api/key/system/escrow-publickey`
 * @param passphrase - the user's vault passphrase (UTF-8 string)
 * @param userEmail - the user's email, bound into the AAD. Must match the
 *   server-side JWT principal email at decryption time.
 * @returns recovery bundle ready to JSON-stringify + base64nopad-encode for
 *   the QR payload
 * @throws {RecoveryError} reason='invalid-public-key' on wrong-size key
 *   (delegated to mlkem's `InvalidMlKemPublicKeyError`)
 * @throws {RecoveryError} reason='invalid-email' on empty / oversized email
 */
export const encryptForEscrow = (
  rng: Random,
  systemKemPublicKey: Uint8Array,
  passphrase: string,
  userEmail: string
): RecoveryBundle => {
  const aad = buildRecoveryAad(userEmail);

  let kemResult;
  try {
    kemResult = kemEncapsulate(rng, systemKemPublicKey);
  } catch (cause) {
    throw new RecoveryError(
      'invalid-public-key',
      cause instanceof Error ? cause.message : 'ML-KEM encapsulation failed',
      { cause }
    );
  }

  const kek = hkdf(
    sha256,
    kemResult.sharedSecret,
    undefined, // salt: zero-filled (no per-message salt needed; nonce + AAD provide message uniqueness)
    RECOVERY_DOMAIN_BYTES, // info: 'BD-recovery-v1' as bytes
    KEK_LENGTH_BYTES
  );

  const nonce = rng.getRandomBytes(NONCE_LENGTH_BYTES);
  const wrappedPassphrase = aeadEncrypt({
    key: kek,
    nonce,
    plaintext: new TextEncoder().encode(passphrase),
    aad,
  });

  return {
    algorithm: ESCROW_ALGORITHM_ID,
    kemCiphertext: base64nopad.encode(kemResult.ciphertext),
    nonce: base64nopad.encode(nonce),
    wrappedPassphrase: base64nopad.encode(wrappedPassphrase),
  };
};

// -----------------------------------------------------------------------------
// Decrypt (server-side reference + frontend tests)
// -----------------------------------------------------------------------------

/**
 * Decrypt a recovery bundle back to the plaintext passphrase. This is the
 * server-side operation; the frontend only uses it for round-trip tests.
 *
 * @param systemKemPrivateKey - 2400-byte ML-KEM-768 private key (server-only)
 * @param bundle - the on-wire bundle (will be schema-validated)
 * @param userEmail - the email from the authenticated JWT principal; must
 *   match the email bound at encryption time or AEAD-tag verification fails
 * @returns the recovered passphrase (UTF-8 string)
 * @throws {RecoveryError} reason='invalid-bundle-shape' on schema failure
 * @throws {RecoveryError} reason='invalid-private-key' on wrong-size key
 * @throws {RecoveryError} reason='authentication-failed' on AEAD-tag failure
 *   (bundle does not belong to this user, or is corrupted)
 */
export const decryptFromEscrow = (
  systemKemPrivateKey: Uint8Array,
  bundle: unknown,
  userEmail: string
): string => {
  const parsed = RecoveryBundleSchema.safeParse(bundle);
  if (!parsed.success) {
    throw new RecoveryError(
      'invalid-bundle-shape',
      `recovery bundle rejected: ${parsed.error.message}`,
      { cause: parsed.error }
    );
  }
  const { kemCiphertext, nonce, wrappedPassphrase } = parsed.data;

  const aad = buildRecoveryAad(userEmail);

  let sharedSecret: Uint8Array;
  try {
    sharedSecret = kemDecapsulate(
      systemKemPrivateKey,
      base64nopad.decode(kemCiphertext)
    );
  } catch (cause) {
    // Wrong-size private key or wrong-size ciphertext (caller bugs).
    // Tag-verify failures come from the AEAD step below, not here —
    // ML-KEM uses implicit rejection.
    throw new RecoveryError(
      'invalid-private-key',
      cause instanceof Error ? cause.message : 'ML-KEM decapsulation failed',
      { cause }
    );
  }

  const kek = hkdf(
    sha256,
    sharedSecret,
    undefined,
    RECOVERY_DOMAIN_BYTES,
    KEK_LENGTH_BYTES
  );

  let plaintext: Uint8Array;
  try {
    plaintext = aeadDecrypt({
      key: kek,
      nonce: base64nopad.decode(nonce),
      ciphertext: base64nopad.decode(wrappedPassphrase),
      aad,
    });
  } catch (cause) {
    if (cause instanceof AeadAuthenticationError) {
      throw new RecoveryError(
        'authentication-failed',
        'recovery bundle does not belong to this account or is corrupted',
        { cause }
      );
    }
    throw cause;
  }

  return new TextDecoder('utf-8', { fatal: true }).decode(plaintext);
};
